import { TradeCard as ApiTradeCard, SetDetail, MatchIndexPackage } from 'store/services/trade/tradeApi';
import { TradeCard as ModalTradeCard } from '../components/InitiateTradeModal';
import { FinishSource, finishFields, fromHaveFinish, getAvailableFinishes, HaveFinish, haveFinishFromRow, offerableFinishes, toHaveFinish } from 'utils/finishes';

// One physical send routed to a specific want.
export interface AutoMatchAssignment {
  wantListId: number;
  haveRecordId: string;
  compositeId: string;
  cardId: number;
  /** Derived mirror of `finish` */
  foil: boolean;
  /** The have row's finish (Track D); legacy match details derive it from foil */
  finish: HaveFinish;
  condition: string;
}

// Runtime-augmented card with auto-match metadata
export interface AutoMatchedCard extends ApiTradeCard {
  _autoMatchHaveCardId?: string;      // sender's printing MTGCard.id
  _autoMatchCompositeId?: string;     // representative compositeId (first assignment in group)
  _autoMatchAssignments?: AutoMatchAssignment[];  // one per sendable copy; drives trade-initiation routing
}

// ── 1. Expansion Logic — collapsed per (printing × foil), greedy-routed ────
//
// Input is the backend's compositeId list for this package (each "want-have"
// pair the backend says is compatible), matchDetails (cardId/foil/condition per
// composite), and haveQtyByCardId (sender's owned copies per printing, keyed by
// MTGCard.id). The backend over-emits composites: a single physical printing
// matching N wants appears N times, and it emits ONE composite per printing
// regardless of how many copies the sender owns. We:
//   1. Run greedy bipartite matching. Each printing (have-record) may route up
//      to its owned-copy count (haveQtyByCardId); each routed copy consumes one
//      unit of a want that still has remaining quantity. So a printing yields
//      min(owned copies, remaining want capacity) assignments. Unroutable copies
//      are dropped.
//   2. Group routed assignments by (cardId, foil).
//   3. Emit one AutoMatchedCard per group, quantity = group size = min(available,
//      wanted), carrying the list of assignments so trade-initiation can emit one
//      API row per copy.
export function expandAutoMatchCards(
  valuedCards: ApiTradeCard[],
  compositeIds: string[],
  matchDetails: Record<string, { condition: string; foil: boolean; cardId: number }> | undefined,
  haveQtyByCardId: Map<number, number>,
): AutoMatchedCard[] {
  if (compositeIds.length === 0) return [];

  // Collect every candidate assignment per physical have record.
  const haveToCandidates = new Map<string, AutoMatchAssignment[]>();
  for (const cid of compositeIds) {
    const dash = cid.indexOf('-');
    if (dash === -1) continue;
    const wantListId = parseInt(cid.substring(0, dash), 10);
    const haveRecordId = cid.substring(dash + 1);
    const details = matchDetails?.[cid];
    if (!details) continue;
    const cand: AutoMatchAssignment = {
      wantListId,
      haveRecordId,
      compositeId: cid,
      cardId: details.cardId,
      ...finishFields(haveFinishFromRow(details)),
      condition: details.condition,
    };
    const list = haveToCandidates.get(haveRecordId);
    if (list) list.push(cand); else haveToCandidates.set(haveRecordId, [cand]);
  }

  // Greedy route. Want capacity seeded from valuedCards.quantity (the receiver's
  // wanted count). Use `?? 1` so an explicit quantity of 0 is honoured (no
  // routes), while undefined defaults to 1.
  const wantRemaining = new Map<number, number>();
  for (const c of valuedCards) {
    wantRemaining.set(c.wantListId, c.quantity ?? 1);
  }

  // Available copies remaining per printing (MTGCard.id), shared across every
  // have-record of the same printing so duplicate records never double-count
  // against the sender's owned quantity.
  const cardRemaining = new Map<number, number>();
  const assignments: AutoMatchAssignment[] = [];
  haveToCandidates.forEach(candidates => {
    const cardId = candidates[0].cardId;
    if (!cardRemaining.has(cardId)) {
      // `?? 1` preserves the legacy one-copy behaviour when the have-list
      // summary hasn't loaded yet.
      cardRemaining.set(cardId, haveQtyByCardId.get(cardId) ?? 1);
    }
    // Route each owned copy to the first candidate want that still has capacity,
    // so the group ends up with min(owned copies, remaining want capacity).
    while ((cardRemaining.get(cardId) ?? 0) > 0) {
      let routed = false;
      for (const cand of candidates) {
        const remaining = wantRemaining.get(cand.wantListId) ?? 0;
        if (remaining > 0) {
          wantRemaining.set(cand.wantListId, remaining - 1);
          cardRemaining.set(cardId, (cardRemaining.get(cardId) ?? 0) - 1);
          assignments.push(cand);
          routed = true;
          break;
        }
      }
      if (!routed) break; // no want has remaining capacity → stop routing this have
    }
  });

  // Group routed assignments by (cardId, finish): the lowercase finish word is the
  // key segment (Track D), so an etched copy never groups with a foil one.
  const groups = new Map<string, AutoMatchAssignment[]>();
  for (const a of assignments) {
    const key = `${a.cardId}|${fromHaveFinish(a.finish)}`;
    const list = groups.get(key);
    if (list) list.push(a); else groups.set(key, [a]);
  }

  const expanded: AutoMatchedCard[] = [];
  groups.forEach(groupAssignments => {
    const first = groupAssignments[0];
    // Representative valuedCard: prefer one whose acceptableSetDetails includes
    // this printing (gives correct image via resolveAutoMatchImageUrl), else
    // the valuedCard matching the first assignment's wantListId, else any.
    const representative =
      valuedCards.find(c => c.acceptableSetDetails?.some(d => d.cardId === first.cardId))
      ?? valuedCards.find(c => c.wantListId === first.wantListId)
      ?? valuedCards[0];
    if (!representative) return;

    expanded.push({
      ...representative,
      quantity: groupAssignments.length,
      ...finishFields(first.finish),
      wantListId: first.wantListId,
      _autoMatchHaveCardId: String(first.cardId),
      _autoMatchCompositeId: first.compositeId,
      _autoMatchAssignments: groupAssignments,
    });
  });

  return expanded;
}

// ── 2. Image Resolution (v5) ───────────────────────────────────────────────

export interface HaveMatchEntry {
  haveCardId: string;
  condition?: string;
  foil?: boolean;
  /** Track D; absent on legacy match details (derive from foil) */
  finish?: HaveFinish | null;
}

export function resolveAutoMatchImageUrl(
  card: AutoMatchedCard,
  haveMatchMap?: Map<string, HaveMatchEntry>,
): string | undefined {
  // Check _autoMatchHaveCardId first (from expansion)
  const storedHaveCardId = card._autoMatchHaveCardId;

  if (storedHaveCardId && card.acceptableSetDetails) {
    const setDetail = card.acceptableSetDetails.find(
      (d) => String(d.cardId) === String(storedHaveCardId)
    );
    if (setDetail?.imageUrl) {
      return setDetail.imageUrl;
    }
  }

  // Fallback: Use haveMatchMap
  if (!haveMatchMap) return undefined;
  const haveMatch = haveMatchMap.get(String(card.wantListId));
  if (!haveMatch) return undefined;
  const setDetail = card.acceptableSetDetails?.find(
    (d) => String(d.cardId) === haveMatch.haveCardId
  );
  return setDetail?.imageUrl;
}

// ── 3. Build Have Match Map ────────────────────────────────────────────────

export function buildHaveMatchMapFromPackage(
  packageMatch: MatchIndexPackage | undefined,
): Map<string, HaveMatchEntry> {
  const map = new Map<string, HaveMatchEntry>();
  if (!packageMatch) return map;

  for (const compositeId of packageMatch.matchingCardIds) {
    const dashIdx = compositeId.indexOf('-');
    if (dashIdx === -1) continue;
    const wantListId = compositeId.substring(0, dashIdx);
    const details = packageMatch.matchDetails?.[compositeId];
    const haveCardId = String(details?.cardId ?? compositeId.substring(dashIdx + 1));
    if (!map.has(wantListId)) {
      map.set(wantListId, {
        haveCardId,
        condition: details?.condition,
        foil: details?.foil,
        finish: details?.finish,
      });
    }
  }
  return map;
}

// ── 4. Build Modal Trade Card (v7) ─────────────────────────────────────────

export function buildModalTradeCard(
  card: AutoMatchedCard,
  haveMatch: HaveMatchEntry | undefined,
  getSetCode: (setName: string) => string,
  isUUID: (str: string) => boolean,
): ModalTradeCard {
  // Check price availability
  const hasNonFoil = !!(card.nonFoilPointValue && card.nonFoilPointValue > 0);
  const hasFoil = !!(card.foilPointValue && card.foilPointValue > 0);

  let acceptableSetDetails = card.acceptableSetDetails;

  // Only create our own acceptableSetDetails if backend doesn't provide it or it's empty
  if (!acceptableSetDetails || acceptableSetDetails.length === 0) {
    acceptableSetDetails = card.acceptableSets?.map((setName: string, index: number) => {
      const backendCode = (card as any).acceptableSetCodes?.[index];
      if (backendCode) {
        return { name: setName, code: backendCode, cardId: 0 };
      }
      const mappedCode = getSetCode(setName);
      if (mappedCode && mappedCode !== setName) {
        return { name: setName, code: mappedCode, cardId: 0 };
      }
      return { name: setName, code: setName, cardId: 0 };
    });
  }

  // Auto-match: find sender's specific printing in acceptableSetDetails
  let autoMatchSetDetail: SetDetail | undefined;
  if (haveMatch && acceptableSetDetails && acceptableSetDetails.length > 0) {
    autoMatchSetDetail = acceptableSetDetails.find(
      (d: SetDetail) => String(d.cardId) === haveMatch.haveCardId
    );
  }

  // --- SET defaults ---
  let finalSetCode: string = '';
  let finalSetName: string = card.set;
  let finalImageUrl: string = card.imageUrl;
  let finalCardId: number = card.cardId;

  if (autoMatchSetDetail) {
    finalSetCode = autoMatchSetDetail.code;
    finalSetName = autoMatchSetDetail.name;
    if (autoMatchSetDetail.imageUrl) {
      finalImageUrl = autoMatchSetDetail.imageUrl;
    }
    if (autoMatchSetDetail.cardId) {
      finalCardId = autoMatchSetDetail.cardId;
    }
  } else {
    const isSetCodeUUID = card.setCode && isUUID(card.setCode);
    if (!card.setCode || isSetCodeUUID) {
      if (acceptableSetDetails && acceptableSetDetails.length > 0) {
        const currentSetDetail = acceptableSetDetails.find((detail: any) => detail.name === card.set);
        if (currentSetDetail && currentSetDetail.code) {
          finalSetCode = currentSetDetail.code;
        } else {
          finalSetCode = acceptableSetDetails[0].code;
        }
      } else {
        const mapped = getSetCode(card.set);
        if (mapped && mapped !== card.set) {
          finalSetCode = mapped;
        } else {
          finalSetCode = card.set;
        }
      }
    } else {
      finalSetCode = card.setCode;
    }
  }

  // --- FINISH defaults (three-value, Track D) ---
  // The sender's matched copy keeps its finish when the want accepts it; otherwise
  // the first finish (canonical order) the printing has and the want accepts.
  const finishSource: FinishSource = autoMatchSetDetail?.availableFinishes
    ? { finishes: autoMatchSetDetail.availableFinishes }
    : {
        availableFinishes: card.availableFinishes,
        availableInFoil: card.availableInFoil ?? hasFoil,
        availableInNonFoil: card.availableInNonFoil ?? hasNonFoil,
      };
  const offerable = offerableFinishes(finishSource, card);
  const fallbackFinish = toHaveFinish(offerable[0] ?? getAvailableFinishes(finishSource)[0]);
  let defaultFinish: HaveFinish = fallbackFinish;
  if (haveMatch && (haveMatch.finish || haveMatch.foil !== undefined)) {
    const matched = haveFinishFromRow(haveMatch);
    defaultFinish = offerable.includes(fromHaveFinish(matched)) ? matched : fallbackFinish;
  }
  const pointsByFinish: Record<'nonfoil' | 'foil' | 'etched', number | undefined> = {
    nonfoil: card.nonFoilPointValue,
    foil: card.foilPointValue,
    etched: card.etchedPointValue ?? undefined,
  };

  // --- CONDITION defaults ---
  const acceptableConditions = card.acceptableConditions;
  let defaultCondition = acceptableConditions?.[0] || 'NEAR_MINT';
  if (haveMatch?.condition) {
    if (!acceptableConditions || acceptableConditions.length === 0 ||
        acceptableConditions.includes(haveMatch.condition)) {
      defaultCondition = haveMatch.condition;
    }
  }

  // Calculate initial value from the default finish's own points
  const initialValue = pointsByFinish[fromHaveFinish(defaultFinish)] || card.pointValue;

  return {
    id: card.id,
    cardId: finalCardId,
    qty: card.quantity,
    maxQty: card.quantity,
    name: card.name,
    set: finalSetName,
    setCode: finalSetCode || card.set,
    condition: defaultCondition,
    language: card.language,
    ...finishFields(defaultFinish),
    initialFinish: defaultFinish,
    edition: card.edition || '',
    image: finalImageUrl,
    imageSmall: finalImageUrl?.replace('/large/', '/small/').replace('/normal/', '/small/'),
    value: initialValue,
    baseValue: card.pointValue,
    foilValue: card.foilPointValue,
    nonFoilValue: card.nonFoilPointValue,
    etchedValue: card.etchedPointValue ?? undefined,
    availableFinishes: getAvailableFinishes(finishSource),
    acceptableSets: card.acceptableSets,
    acceptableSetCodes: (card as any).acceptableSetCodes,
    acceptableSetDetails: acceptableSetDetails,
    wantListId: card.wantListId,
    availableInFoil: hasFoil,
    availableInNonFoil: hasNonFoil,
    acceptFoil: card.acceptFoil,
    acceptNonFoil: card.acceptNonFoil,
    acceptEtched: card.acceptEtched,
    acceptableConditions: card.acceptableConditions,
    scryfallId: card.scryfallId,
    availableLanguages: (card as any).availableLanguages,
    acceptableLanguages: (card as any).acceptableLanguages,
  };
}

// ── 5. Canonical selection copy-keys ───────────────────────────────────────
//
// The ONE key space for send-page selection state. In auto-match mode the
// rendered cards are expandAutoMatchCards products whose identity is
// _autoMatchCompositeId; outside auto-match it is the want-row id. Selection
// state must be produced and consumed exclusively through these helpers, and
// always over the RENDERED card collection (ProcessedPackage.valuedCards),
// never raw API packages — raw want rows and expanded groups disagree on both
// membership and keys.

export function sendCardCopyKey(card: ApiTradeCard, copyIndex: number): string {
  return `${(card as AutoMatchedCard)._autoMatchCompositeId ?? card.id}-${copyIndex}`;
}

export function sendCardCopyKeys(card: ApiTradeCard): string[] {
  return Array.from({ length: card.quantity ?? 1 }, (_, i) => sendCardCopyKey(card, i));
}

// Selection set for a first interaction: every rendered copy except the
// excluded key(s) — the copy (or whole card) the user just deselected.
export function buildSelectionAllExcept(
  cards: ApiTradeCard[],
  excluded: string | ReadonlySet<string>,
): Set<string> {
  const excludedSet = typeof excluded === 'string' ? new Set([excluded]) : excluded;
  const selected = new Set<string>();
  for (const card of cards) {
    for (const key of sendCardCopyKeys(card)) {
      if (!excludedSet.has(key)) selected.add(key);
    }
  }
  return selected;
}

// Reduce a rendered card list to the selected copies. Quantity becomes the
// selected-copy count, and _autoMatchAssignments (one entry per copy, index-
// aligned with the copy key index) is sliced to the surviving copies so
// downstream per-copy routing sends exactly what was selected.
export function filterCardsBySelection<T extends ApiTradeCard>(
  cards: T[],
  selected: ReadonlySet<string>,
): T[] {
  const result: T[] = [];
  for (const card of cards) {
    const selectedIndices: number[] = [];
    for (let i = 0; i < (card.quantity ?? 1); i++) {
      if (selected.has(sendCardCopyKey(card, i))) selectedIndices.push(i);
    }
    if (selectedIndices.length === 0) continue;
    const assignments = (card as unknown as AutoMatchedCard)._autoMatchAssignments;
    const filtered: T = {
      ...card,
      quantity: selectedIndices.length,
      ...(assignments && assignments.length > 0
        ? { _autoMatchAssignments: selectedIndices.map(i => assignments[i]).filter(Boolean) }
        : {}),
    };
    result.push(filtered);
  }
  return result;
}

// ── Auto-match price batching ───────────────────────────────────────────────
// The backend rejects a calculate-price request over its cap with a 400. Before
// chunking, one oversized request was answered with a 200 full of zeros, and those
// zeros overwrote every correct base value on /sendcards. See CALCULATE_PRICE_BATCH_SIZE
// in SendPage2.tsx and magictrade-backend PriceValidationService.ts:59-62.

// Minimal shape the price merge needs; the caller's entries carry more fields.
export interface AutoMatchPriceEntry {
  compositeId: string;
}

export interface AutoMatchPriceCard {
  /** Null when the printing has no price (the slot then carries a code). */
  displayPrice: number | null;
}

/** Split price-request entries into batches of at most `size`. */
export function chunkPriceRequest<T>(entries: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < entries.length; i += size) {
    chunks.push(entries.slice(i, i + size));
  }
  return chunks;
}

/**
 * Merge settled per-chunk price responses into one compositeId -> price map.
 *
 * A rejected chunk contributes nothing, so its rows keep their base pointValue;
 * the surviving chunks still apply. The map is rebuilt from scratch each run
 * rather than merged into the previous one, because the entry list changes with
 * the page and retained entries would be stale prices for rows no longer shown.
 */
export function mergeChunkedPrices<T extends AutoMatchPriceEntry>(
  chunks: T[][],
  settled: Array<PromiseSettledResult<{ cards: AutoMatchPriceCard[] }>>,
  onError?: (message: string, detail?: unknown) => void,
): Map<string, number | null> {
  // Slots pass through as sent, null and 0 included: the overlay decides, and it
  // applies only a real price (hasRealPrice), keeping the row's base value otherwise.
  const priceMap = new Map<string, number | null>();

  settled.forEach((outcome, chunkIndex) => {
    const chunk = chunks[chunkIndex] || [];

    if (outcome.status === 'rejected') {
      onError?.('Failed to fetch auto-match printing prices (chunk):', outcome.reason);
      return;
    }

    // The contract is one entry per request card, in request order. A short or
    // reordered response would silently drop or misplace prices - the exact class
    // of defect this code exists to prevent - so it is loud rather than absorbed.
    if (outcome.value.cards.length !== chunk.length) {
      onError?.(
        `calculate-price returned ${outcome.value.cards.length} entries for a ` +
        `${chunk.length}-card chunk; prices may be misaligned.`,
      );
    }

    outcome.value.cards.forEach((cardPrice, index) => {
      const entry = chunk[index];
      if (entry) priceMap.set(entry.compositeId, cardPrice.displayPrice);
    });
  });

  return priceMap;
}
